// Pure helper that turns a visit date into a "smart" relative-recency
// descriptor — Today / Yesterday / N days / N weeks / N months / N years ago.
// No React, no i18n — kept here as the single source of truth so the visit
// timeline rows and the consult header/nav labels compute recency identically.
//
// The result is an i18n key (+ optional `count` for pluralised keys); callers
// resolve it with their `t` function. `formatVisitRecency` does that resolution
// for the common case. `nowMs` is injectable for deterministic tests.

import type { TFunction } from 'i18next';

export interface VisitRecency {
  key: string;
  count?: number;
}

const DAY_MS = 86_400_000;
const AVG_MONTH_DAYS = 30.44;
const AVG_YEAR_DAYS = 365.25;

/** Local-midnight Date for `d`, so differences count whole calendar days. */
function startOfLocalDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

/**
 * Whole calendar days between two dates. Uses `Math.round` on the millisecond
 * gap between local midnights so a 23h/25h daylight-saving day still resolves to
 * an integer day count.
 */
function calendarDaysBetween(from: Date, to: Date): number {
  return Math.round((startOfLocalDay(to).getTime() - startOfLocalDay(from).getTime()) / DAY_MS);
}

/**
 * Relative recency of `visit` measured against `nowMs` (defaults to now). Future
 * dates and same-day visits collapse to "today" so clock skew never renders a
 * negative count.
 */
export function visitRecency(visit: Date, nowMs: number = Date.now()): VisitRecency {
  const days = calendarDaysBetween(visit, new Date(nowMs));
  if (days <= 0) return { key: 'patients.visitToday' };
  if (days === 1) return { key: 'patients.visitYesterday' };
  if (days < 7) return { key: 'patients.visitDaysAgo', count: days };
  if (days < 30) return { key: 'patients.visitWeeksAgo', count: Math.round(days / 7) };
  const months = Math.round(days / AVG_MONTH_DAYS);
  if (months < 12) return { key: 'patients.visitMonthsAgo', count: Math.max(1, months) };
  return { key: 'patients.visitYearsAgo', count: Math.max(1, Math.round(days / AVG_YEAR_DAYS)) };
}

/** Resolve the recency of `visit` to a translated, display-ready string. */
export function formatVisitRecency(visit: Date, t: TFunction, nowMs?: number): string {
  const { key, count } = visitRecency(visit, nowMs);
  return count !== undefined ? t(key, { count }) : t(key);
}
